import type { TocItemData } from '~/utils/literature/hierarchy-builder'
import { removeExistingHighlights } from '~/utils/solr/highlighting-utils'

export interface NearestHeadingInput {
  bodytext: string
  fragment: string | undefined
  tocItems: TocItemData[]
  pageNumberString: string | null
}

const HEADING_ANCHOR_RE = /<h[1-6]\b[^>]*?\sid="([^"]{1,64})"/g

// Any heading marks a section, only anchor lookups need an id.
const HEADING_RE = /<h[1-6]\b/

interface AnchorScan {
  anchor: string | null
  matchPrecedesFirstHeading: boolean
}

function findAnchorBeforeMatch(bodytext: string, fragment: string | undefined): AnchorScan {
  if (!fragment) {
    return { anchor: null, matchPrecedesFirstHeading: false }
  }

  const offset = bodytext.indexOf(removeExistingHighlights(fragment))
  if (offset === -1) {
    return { anchor: null, matchPrecedesFirstHeading: false }
  }

  // Stop at the matched word, not the fragment start. Fragments can cross headings, so stopping early may select the wrong section.
  const marker = fragment.indexOf('<span class="highlight">')
  const scanEnd = marker === -1 ? offset : offset + marker

  const beforeMatch = bodytext.slice(0, scanEnd)

  let anchor: string | null = null
  for (const match of beforeMatch.matchAll(HEADING_ANCHOR_RE)) {
    anchor = match[1] ?? null
  }

  return {
    anchor,
    matchPrecedesFirstHeading: !HEADING_RE.test(beforeMatch) && HEADING_RE.test(bodytext.slice(scanEnd)),
  }
}

function findTocEntryByPage(
  tocItems: TocItemData[],
  pageNumberString: string | null,
  excludeTargetPage = false,
): TocItemData | null {
  const targetPage = Number.parseInt(pageNumberString ?? '', 10)
  if (Number.isNaN(targetPage)) {
    return null
  }

  // A match above this page's first heading belongs to a section that opened on an earlier page.
  const lastEligiblePage = excludeTargetPage ? targetPage - 1 : targetPage

  let bestMatch: TocItemData | null = null
  let bestMatchPage = Number.NEGATIVE_INFINITY

  // Keep the later entry, pages without a heading use the latest section.
  for (const item of tocItems) {
    const page = Number.parseInt(item.pageNumberString ?? '', 10)
    if (Number.isNaN(page) || page > lastEligiblePage || page < bestMatchPage) {
      continue
    }
    bestMatch = item
    bestMatchPage = page
  }

  return bestMatch
}

export function resolveNearestHeading(input: NearestHeadingInput): string | null {
  const { anchor, matchPrecedesFirstHeading } = findAnchorBeforeMatch(input.bodytext, input.fragment)
  if (anchor) {
    const byAnchor = input.tocItems.find(item => item.fragmentId === anchor)
    if (byAnchor) {
      return byAnchor.title
    }
  }

  return findTocEntryByPage(input.tocItems, input.pageNumberString, matchPrecedesFirstHeading)?.title ?? null
}
